import type { BedConfig, BedSpace, SpaceKind, UnitShape } from '@lemontaigu/pms-core';
import { KITCHEN_LABELS_FR, SPACE_KIND_LABELS_FR, pmsBedCapacity } from '@lemontaigu/pms-core';
import { BedIcons } from './RoomIcons.js';
import { bedLayoutFor, bedLayoutLookup } from './logic.js';

// The apartment half of a unit, shared by the fiche (editing) and « Nouvel
// appartement » (creating).
//
// Extracted rather than copied because the two forms must agree on what a unit
// IS: the create form that writes a composition the fiche cannot render — or
// vice versa — is a bug nobody would notice until an apartment looked wrong.
// One definition, two mounts.

const SPACE_KINDS: SpaceKind[] = ['bedroom', 'living', 'other'];
const KITCHENS = ['none', 'kitchenette', 'full'] as const;

/** '' → null, so an emptied number field means « not stated » rather than 0.
 *  A hotel room does not have zero bedrooms; it has no opinion. */
export const numOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export interface UnitScalarsProps {
  unit: UnitShape;
  disabled: boolean;
  onChange: (patch: Partial<UnitShape>) => void;
}

export function UnitScalars({ unit, disabled, onChange }: UnitScalarsProps) {
  return (
    <>
      <label>
        Chambres
        <input
          type="number"
          min={0}
          value={unit.bedrooms ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ bedrooms: numOrNull(e.target.value) })}
        />
      </label>
      <label>
        Séjours
        <input
          type="number"
          min={0}
          value={unit.livingRooms ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ livingRooms: numOrNull(e.target.value) })}
        />
      </label>
      <label>
        Salles de bain
        <input
          type="number"
          min={0}
          value={unit.bathrooms ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ bathrooms: numOrNull(e.target.value) })}
        />
      </label>
      <label>
        WC
        <input
          type="number"
          min={0}
          value={unit.wcCount ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ wcCount: numOrNull(e.target.value) })}
        />
      </label>
      <label>
        Cuisine
        <select
          value={unit.kitchen ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({ kitchen: e.target.value === '' ? null : (e.target.value as UnitShape['kitchen']) })
          }
        >
          <option value="">—</option>
          {KITCHENS.map((k) => (
            <option key={k} value={k}>
              {KITCHEN_LABELS_FR[k]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Surface (m²)
        <input
          type="number"
          min={0}
          step="0.5"
          value={unit.surfaceSqm ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ surfaceSqm: numOrNull(e.target.value) })}
        />
      </label>
    </>
  );
}

export interface SpaceEditorProps {
  spaces: BedSpace[];
  bedConfigs: BedConfig[];
  /** The operator's declared capacity, for the derived-bed cross-check. */
  maxOccupancy: number;
  disabled: boolean;
  onChange: (next: BedSpace[]) => void;
}

export function SpaceEditor({
  spaces,
  bedConfigs,
  maxOccupancy,
  disabled,
  onChange,
}: SpaceEditorProps) {
  const lookup = bedLayoutLookup(bedConfigs);

  // Derived and ONLY a warning. Booking.com lets a 4-bed apartment cap at 5
  // with an extra bed, or at 4 despite a sofa bed, so this figure and the
  // operator's are two different facts about the same unit (design brief §6.5).
  // Never written back — all three seeded apartments differ, correctly.
  const derived = pmsBedCapacity(spaces, (code) => bedLayoutFor(lookup, code));
  const differs = spaces.length > 0 && derived !== maxOccupancy;

  const at = (i: number, patch: Partial<BedSpace>) =>
    onChange(spaces.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const add = () => {
    const kind: SpaceKind = 'bedroom';
    const next = Math.max(0, ...spaces.filter((s) => s.spaceKind === kind).map((s) => s.spaceIndex)) + 1;
    onChange([
      ...spaces,
      { spaceKind: kind, spaceIndex: next, bedConfig: bedConfigs[0]?.code ?? '', quantity: 1 },
    ]);
  };

  return (
    <>
      <p className="pms-sheet-section">
        Couchages par pièce
        {differs && (
          <span className="pms-hint">
            {' '}— les lits couchent {derived} personne{derived > 1 ? 's' : ''}, la capacité
            annoncée est {maxOccupancy}. À vérifier.
          </span>
        )}
      </p>

      {spaces.length === 0 && (
        <p className="pms-hint pms-sheet-wide">
          Aucune pièce renseignée — un logement doit décrire ses couchages, pièce par pièce.
        </p>
      )}

      {/* The four controls carried aria-labels but nothing a sighted operator
          could read, so « Chambre | 1 | Double | 1 » gave no clue that the first
          number is WHICH bedroom and the second is HOW MANY of that bed. That
          ambiguity is not cosmetic: two rows left at « Chambre 1 » collide on
          pms_room_bed_space_uq and the save is refused. Reported 2026-08-11. */}
      {spaces.length > 0 && (
        <div className="pms-sheet-space pms-sheet-space-head" aria-hidden="true">
          <span>Pièce</span>
          <span>N°</span>
          <span>Lits</span>
          <span>Nombre</span>
          <span />
          <span />
        </div>
      )}

      {spaces.map((s, i) => (
        <div className="pms-sheet-space" key={`${s.spaceKind}-${s.spaceIndex}-${i}`}>
          <select
            aria-label="Pièce"
            value={s.spaceKind}
            disabled={disabled}
            onChange={(e) => at(i, { spaceKind: e.target.value as SpaceKind })}
          >
            {SPACE_KINDS.map((k) => (
              <option key={k} value={k}>
                {SPACE_KIND_LABELS_FR[k]}
              </option>
            ))}
          </select>
          <input
            aria-label="Numéro de pièce"
            type="number"
            min={1}
            value={s.spaceIndex}
            disabled={disabled}
            onChange={(e) => at(i, { spaceIndex: Number(e.target.value) })}
          />
          <select
            aria-label="Lits de la pièce"
            value={s.bedConfig}
            disabled={disabled}
            onChange={(e) => at(i, { bedConfig: e.target.value })}
          >
            {bedConfigs.map((b) => (
              <option key={b.code} value={b.code}>
                {b.code} — {b.labelFr}
              </option>
            ))}
          </select>
          <input
            aria-label="Quantité"
            type="number"
            min={1}
            value={s.quantity}
            disabled={disabled}
            onChange={(e) => at(i, { quantity: Number(e.target.value) })}
          />
          <BedIcons layouts={[{ code: s.bedConfig, layout: bedLayoutFor(lookup, s.bedConfig) }]} />
          {!disabled && (
            <button
              type="button"
              className="pms-link is-danger"
              aria-label="Retirer la pièce"
              onClick={() => onChange(spaces.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          )}
        </div>
      ))}

      {!disabled && (
        <button type="button" className="pms-link pms-sheet-wide" onClick={add}>
          + Ajouter une pièce
        </button>
      )}
    </>
  );
}
